import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Package, Plus, Trash2, AlertTriangle, Minus } from "lucide-react";
import { API, auth, inputClass, labelClass, primaryBtn, ghostBtn, handleAuthError } from "./shared";
import { useConfirm } from "./useConfirm";

const empty = { name: "", ref: "", qty: 0, min_qty: 1, unit_price: 0 };

const AdminParts = ({ token, logout }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const { confirm, dialog } = useConfirm();

  const load = () => axios.get(`${API}/parts`, auth(token)).then((r) => setItems(r.data)).catch((err) => handleAuthError(err, logout, () => {}));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/parts`, { ...form, qty: Number(form.qty), min_qty: Number(form.min_qty), unit_price: Number(form.unit_price) }, auth(token));
      toast.success("Pièce ajoutée");
      setForm(empty);
      load();
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Échec de l'ajout"));
    }
  };

  const adjust = async (p, delta) => {
    const qty = Math.max(0, p.qty + delta);
    try {
      const r = await axios.patch(`${API}/parts/${p.id}`, { name: p.name, ref: p.ref, qty, min_qty: p.min_qty, unit_price: p.unit_price }, auth(token));
      if (r.data.low && !p.low) toast.warning(`Stock bas : ${p.name} — alerte envoyée au directeur`);
      load();
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Échec de la mise à jour"));
    }
  };

  const remove = async (p) => {
    if (!(await confirm({ title: "Supprimer cette pièce ?", description: `${p.name} sera retirée du stock.` }))) return;
    try {
      await axios.delete(`${API}/parts/${p.id}`, auth(token));
      load();
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Échec"));
    }
  };

  const low = items.filter((p) => p.low);

  return (
    <div data-testid="admin-parts-tab">
      {dialog}
      {low.length > 0 && (
        <div className="mb-6 flex items-start gap-3 rounded-2xl bg-cream p-5 text-sm text-forest" data-testid="parts-low-alert">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
          <div><b>{low.length} pièce(s) à réapprovisionner :</b> {low.map((p) => p.name).join(", ")}</div>
        </div>
      )}
      <form onSubmit={add} className="rounded-3xl bg-mint p-8" data-testid="parts-form">
        <h2 className="font-display flex items-center gap-2 text-lg font-bold text-forest"><Package className="h-5 w-5 text-gold" /> Ajouter une pièce</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-5">
          <div className="sm:col-span-2"><label className={labelClass}>Désignation *</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="parts-name-input" className={inputClass} placeholder="Ex : Filtre à huile Toyota" /></div>
          <div><label className={labelClass}>Référence</label><input value={form.ref} onChange={(e) => setForm({ ...form, ref: e.target.value })} data-testid="parts-ref-input" className={inputClass} /></div>
          <div><label className={labelClass}>Quantité</label><input type="number" min="0" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="parts-qty-input" className={inputClass} /></div>
          <div><label className={labelClass}>Seuil d'alerte</label><input type="number" min="0" value={form.min_qty} onChange={(e) => setForm({ ...form, min_qty: e.target.value })} data-testid="parts-min-input" className={inputClass} /></div>
          <div><label className={labelClass}>Prix unitaire (FCFA)</label><input type="number" min="0" step="500" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} data-testid="parts-price-input" className={inputClass} /></div>
        </div>
        <button type="submit" data-testid="parts-submit-button" className={`mt-6 ${primaryBtn}`}><Plus className="h-4 w-4" /> Ajouter au stock</button>
      </form>

      <h2 className="font-display mt-12 text-lg font-bold text-forest">Stock <span className="text-gold">({items.length})</span></h2>
      <div className="mt-4 overflow-x-auto rounded-3xl border border-brand/10 bg-white">
        <table className="w-full text-sm text-forest">
          <thead className="bg-mint font-mono text-[10px] uppercase tracking-widest text-brand">
            <tr><th className="px-5 py-3 text-left">Pièce</th><th className="px-3 py-3 text-left">Réf.</th><th className="px-3 py-3 text-center">Quantité</th><th className="px-3 py-3 text-right">Seuil</th><th className="px-3 py-3 text-right">P.U.</th><th className="px-3 py-3" /></tr>
          </thead>
          <tbody>
            {items.map((p, i) => (
              <tr key={p.id} data-testid={`part-${i}`} className={`border-t border-brand/5 ${p.low ? "bg-cream/60" : ""}`}>
                <td className="px-5 py-3 font-semibold">{p.low && <AlertTriangle className="mr-1 inline h-4 w-4 text-gold" />}{p.name}</td>
                <td className="px-3 py-3 font-mono text-xs text-brand">{p.ref || "—"}</td>
                <td className="px-3 py-3">
                  <div className="flex items-center justify-center gap-2">
                    <button onClick={() => adjust(p, -1)} data-testid={`part-${i}-minus`} className="flex h-7 w-7 items-center justify-center rounded-full border border-brand/20 text-brand hover:bg-mint"><Minus className="h-3 w-3" /></button>
                    <span data-testid={`part-${i}-qty`} className={`font-display w-8 text-center text-base font-bold ${p.low ? "text-gold" : ""}`}>{p.qty}</span>
                    <button onClick={() => adjust(p, 1)} data-testid={`part-${i}-plus`} className="flex h-7 w-7 items-center justify-center rounded-full border border-brand/20 text-brand hover:bg-mint"><Plus className="h-3 w-3" /></button>
                  </div>
                </td>
                <td className="px-3 py-3 text-right text-brand">{p.min_qty}</td>
                <td className="px-3 py-3 text-right">{Math.round(p.unit_price).toLocaleString("fr-FR")}</td>
                <td className="px-3 py-3 text-right"><button onClick={() => remove(p)} data-testid={`part-${i}-delete`} className={`${ghostBtn} hover:border-gold hover:text-gold`}><Trash2 className="h-3.5 w-3.5" /></button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-brand">Aucune pièce en stock. Ajoutez la première ci-dessus.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminParts;
